import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RevealSection, RevealText } from '@/components/shared/Reveal';
import { usePageMeta } from '@/hooks/usePageMeta';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PacketForm = ({ id, variant, dark }) => {
  const [form, setForm] = useState({ firstName: '', email: '' });
  const [submitted, setSubmitted] = useState(false);

  const lightInput = `w-full px-4 py-3 bg-white/60 border border-vine-neutral/20 text-vine-primary text-[15px] font-body placeholder:text-vine-neutral/40 focus:outline-none focus:border-copper/50 focus:ring-1 focus:ring-copper/20 transition-colors`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.email.trim()) { toast.error('Name and email required.'); return; }
    try {
      await axios.post(`${API}/applications`, { ...form, applicationType: 'packet-download', notify_email: 'rakia@coppervine.io', email_subject: 'Operator Packet Download Request', tag: 'packet-download' });
      setSubmitted(true);
    } catch { toast.error('Something went wrong. Please try again.'); }
  };

  if (submitted) return (
    <div className="py-4">
      <p className={`font-display text-xl font-light mb-2 ${dark ? 'text-vine-warm' : 'text-vine-primary'}`}>Check your inbox.</p>
      <p className={`font-body text-[14px] ${dark ? 'text-vine-neutral/60' : ''}`} style={dark ? {} : {color:'#4a4540'}}>The Operator Packet is on its way.</p>
    </div>
  );

  if (dark) return (
    <form id={id} onSubmit={handleSubmit}>
      <div className="mb-6">
        <label className="block text-[11px] tracking-[0.12em] uppercase mb-2" style={{color:'rgba(255,255,255,0.5)'}}>First Name</label>
        <input type="text" value={form.firstName} onChange={e => setForm(p=>({...p, firstName: e.target.value}))} className="w-full bg-transparent border-0 border-b text-white text-[15px] font-body py-1 focus:outline-none transition-colors" style={{borderBottomColor:'rgba(181,129,59,0.7)', height:'36px'}} onFocus={e => e.target.style.borderBottomColor='#b5813b'} onBlur={e => e.target.style.borderBottomColor='rgba(181,129,59,0.7)'} />
      </div>
      <div className="mb-8">
        <label className="block text-[11px] tracking-[0.12em] uppercase mb-2" style={{color:'rgba(255,255,255,0.5)'}}>Work Email</label>
        <input type="email" value={form.email} onChange={e => setForm(p=>({...p, email: e.target.value}))} className="w-full bg-transparent border-0 border-b text-white text-[15px] font-body py-1 focus:outline-none transition-colors" style={{borderBottomColor:'rgba(181,129,59,0.7)', height:'36px'}} onFocus={e => e.target.style.borderBottomColor='#b5813b'} onBlur={e => e.target.style.borderBottomColor='rgba(181,129,59,0.7)'} />
      </div>
      <button type="submit" className="w-full text-white text-[13px] tracking-[0.1em] uppercase btn-copper inline-flex items-center justify-center gap-1" style={{backgroundColor:'#b5813b', height:'48px'}}>Send Me the Packet <ArrowRight size={13} /></button>
      <p className="text-[12px] text-center mt-4" style={{color:'rgba(255,255,255,0.4)'}}>No spam, ever. Unsubscribe any time.</p>
      <p className="text-[11px] text-center mt-4 leading-relaxed" style={{color:'rgba(255,255,255,0.3)'}}>Used by operators in SaaS, platforms, AI, services, and nonprofits across the US, UK, and West Africa.</p>
    </form>
  );

  return (
    <form id={id} onSubmit={handleSubmit}>
      {variant === 'second' && <p className="font-body text-[15px] mb-5" style={{color:'#3a3530'}}>Still here? Good. That usually means something on this page named a problem you are carrying.</p>}
      <div className="space-y-3 mb-4">
        <input type="text" value={form.firstName} onChange={e => setForm(p=>({...p, firstName: e.target.value}))} className={lightInput} placeholder="First Name" />
        <input type="email" value={form.email} onChange={e => setForm(p=>({...p, email: e.target.value}))} className={lightInput} placeholder="Work Email" />
        <button type="submit" className="w-full px-6 text-white text-[13px] tracking-[0.1em] uppercase btn-copper inline-flex items-center justify-center gap-1" style={{backgroundColor:'#b5813b', height:'48px'}}>Send Me the Packet <ArrowRight size={13} /></button>
      </div>
      <p className="text-vine-primary/30 text-[12px] text-center">No spam, ever. Unsubscribe any time.</p>
    </form>
  );
};

export default function OperatorPacketPage() {
  usePageMeta('Operator Packet \u2014 Advisory Circle \u00b7 Copper & Vine Studio', 'The operating condition that quietly delays every launch, and the framework operators use to get ahead of it.');

  return (
    <div>
      <Toaster position="top-center" />

      {/* ===== 1. HERO — Two Column: Headline Left, Form Right ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 md:pt-32 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left — 60% */}
            <div className="lg:col-span-7">
              <RevealText><p className="text-copper text-[11px] tracking-[0.25em] uppercase font-medium mb-6">Advisory Circle &mdash; Operator Packet</p></RevealText>
              <RevealText delay={100}><h1 className="font-display text-4xl md:text-5xl font-light leading-[1.1] tracking-tight">You Cannot Ship What<br />You Have Not Defined</h1></RevealText>
              <RevealText delay={200}><div className="mt-6 space-y-3">
                <p className="font-body text-vine-warm/75 text-[16px] leading-[1.8]">Most launch delays are not technical failures. They are documentation failures dressed as technical conversations.</p>
                <p className="font-body text-vine-neutral/55 text-[15px] leading-[1.7]">This Packet names the pattern, gives you the diagnostic gate, and tells you the first document to produce before your next high-stakes session.</p>
              </div></RevealText>
              <RevealText delay={300}><p className="mt-6 text-vine-neutral/35 text-[12px] tracking-wide">Produced by Copper &amp; Vine Studio &middot; 20 years &middot; 330 organizations</p></RevealText>
            </div>
            {/* Right — 40% Form */}
            <div className="lg:col-span-5">
              <RevealSection delay={250}>
                <div>
                  <p className="text-copper text-[10px] tracking-[0.2em] uppercase font-medium mb-2">Get the Operator Packet &mdash; Free</p>
                  <p className="font-body text-[13px] leading-relaxed mb-6" style={{color:'rgba(255,255,255,0.75)'}}>One document. Delivered to your inbox in under 60 seconds.</p>
                  <PacketForm id="packet-form" dark />
                </div>
              </RevealSection>
            </div>
          </div>
        </div>
        {/* Copper rule divider */}
        <div className="w-full h-px" style={{backgroundColor:'#b5813b'}} />
      </section>

      {/* ===== 2. WHAT YOU GET ===== */}
      <section className="bg-vine-warm">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-10">
          <RevealSection>
            <p className="text-copper text-[11px] tracking-[0.2em] uppercase font-medium mb-8">One document. Three things inside.</p>
            <div className="space-y-7">
              {[
                { t: 'The Operating Condition', d: 'The specific inflection point where architecture gets ahead of data strategy \u2014 and what it costs when no one has written the requirement down.' },
                { t: 'The Intelligence Brief', d: 'Five operating insights from a real working session. Each one names a pattern, explains why it is expensive, and tells you the one thing to do differently.' },
                { t: 'The Launch Readiness Gate', d: 'A five-question diagnostic to run before any demo, launch, or investor presentation. Every \u201cno\u201d is a risk currently on the table.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <span className="text-copper/50 text-[15px] mt-0.5">&rarr;</span>
                  <div>
                    <p className="font-body text-[15px] font-semibold mb-1" style={{color:'#3a3530'}}>{item.t}</p>
                    <p className="font-body text-[14px] leading-relaxed" style={{color:'#4a4540'}}>{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ===== Social proof bar ===== */}
      <section className="bg-vine-warm border-y border-vine-neutral/10">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-5 text-center">
          <p className="font-body text-[13px]" style={{color:'#4a4540'}}>Used by founders, operators, and executives building revenue-bearing systems in SaaS, platforms, AI, services, and nonprofits across the US, UK, and West Africa.</p>
          <p className="font-body text-[12px] mt-2" style={{color:'#4a4540'}}>Used by operators in global manufacturing, media, government, and social impact organizations.</p>
        </div>
      </section>

      {/* ===== 4. ICP MIRROR ===== */}
      <section className="bg-vine-warm">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-10">
          <RevealSection><p className="text-copper text-[11px] tracking-[0.2em] uppercase font-medium mb-10">This is for you if &mdash;</p></RevealSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: 'The Inflection Founder', d: 'Post-seed to Series A. $1M\u2013$5M ARR. You are the operating backbone \u2014 and starting to feel it. The build is ahead of the documentation.' },
              { t: 'The Scaling Operator', d: 'COO, Chief of Staff, VP Ops. Series A to B. The model that worked at $5M is visibly breaking at $10M. You are building the frameworks your org has never had.' },
              { t: 'The Mission-Driven Executive', d: 'Executive Director, CEO. $2M\u2013$15M budget. The mission is working. The operating infrastructure is not keeping up.' },
            ].map((item, i) => (
              <RevealSection key={i} delay={i * 80}>
                <div className="p-6 h-full rounded-xl" style={{border:'1px solid #d4cfc9'}}>
                  <h3 className="font-display text-lg text-vine-primary mb-3">{item.t}</h3>
                  <p className="font-body text-[14px] leading-relaxed" style={{color:'#3a3530'}}>{item.d}</p>
                </div>
              </RevealSection>
            ))}
          </div>
          <RevealSection>
            <p className="mt-6 font-body text-[16px] italic" style={{color:'#3a3530'}}>This is not for pre-revenue founders or passive readers. It is for operators who are carrying a specific problem right now and are willing to name it in writing.</p>
          </RevealSection>
        </div>
      </section>

      {/* ===== 5. PRACTITIONER QUESTION ===== */}
      <section className="bg-vine-warm">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-14 text-center">
          <RevealSection>
            <div className="w-16 h-px bg-copper/30 mx-auto mb-10" />
            <p className="text-copper text-[10px] tracking-[0.2em] uppercase font-medium mb-6">The Question Inside the Packet</p>
            <p className="font-display text-xl md:text-2xl text-vine-primary/70 italic leading-relaxed max-w-lg mx-auto">&ldquo;If your most important external stakeholder asked you today to walk them through exactly what your product will show on launch day &mdash; using only confirmed data and completed features &mdash; what would you have to remove from that walkthrough?&rdquo;</p>
            <p className="mt-6 font-body text-[15px] font-medium" style={{color:'#3a3530'}}>What you remove is your actual risk inventory.</p>
            <div className="w-16 h-px bg-copper/30 mx-auto mt-10" />
          </RevealSection>
        </div>
      </section>

      {/* ===== 6. PRODUCED BY ===== */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <RevealSection>
            <p className="text-copper text-[11px] tracking-[0.2em] uppercase font-medium mb-5">Produced by Copper &amp; Vine Studio</p>
            <p className="font-body text-vine-neutral/60 text-[15px] leading-relaxed mb-10">For 20 years, Copper &amp; Vine has built governance-first operating systems across 330 organizations &mdash; 150 corporations, 100 nonprofits, 80 startups.</p>
          </RevealSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-0">
            <RevealSection>
              <div className="lg:pr-10 lg:border-r border-white/[0.08]">
                <p className="text-copper text-[10px] tracking-[0.2em] uppercase font-medium mb-2">Founding Partner &amp; CEO</p>
                <h3 className="font-display text-2xl text-vine-warm font-light mb-4">Rakia Finley</h3>
                <p className="font-body text-vine-neutral/60 text-[14px] leading-relaxed mb-3">V1 product engineer for platforms at Starwood Hotels, Apple Watch, and the White House.</p>
                <p className="font-body text-vine-warm/75 text-[14px] leading-relaxed font-medium mb-3">Salesforce Hall of Fame inductee.<br/>North America&rsquo;s first Black female owner of a private AI software firm.</p>
                <p className="font-body text-vine-neutral/65 text-[14px] font-medium mt-4">Every Advisory Circle engagement runs through her directly.</p>
              </div>
            </RevealSection>
            <RevealSection delay={150}>
              <div className="lg:pl-10">
                <p className="text-vine-neutral/40 text-[10px] tracking-[0.2em] uppercase font-medium mb-5">Proof of Work</p>
                <div className="space-y-4">
                  {[
                    { sector: 'Government', result: 'Reduced cycle time 40% through automated governance.' },
                    { sector: 'Enterprise', result: 'Shipped operating model + AI bot for 50,000 employees.' },
                    { sector: 'Creator Brand', result: '100% platform stability at launch. No rebuild loop.' },
                  ].map(item => (
                    <div key={item.sector} className="flex gap-4">
                      <span className="text-copper/50 text-[13px] font-medium w-24 flex-shrink-0">{item.sector}</span>
                      <p className="font-body text-vine-neutral/50 text-[13px]">{item.result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ===== 7. THE NATURAL NEXT STEP ===== */}
      <section className="bg-vine-warm">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-10">
          <RevealSection>
            <p className="text-copper text-[11px] tracking-[0.2em] uppercase font-medium mb-4">What the Packet Is the Front Door To</p>
            <p className="font-body text-[15px] leading-relaxed mb-10" style={{color:'#3a3530'}}>The Operator Packet demonstrates the type of operating intelligence the Advisory Circle delivers for your organization specifically &mdash; every month, for a year.</p>
          </RevealSection>

          <RevealSection>
            <div className="w-full h-px bg-copper/15 mb-8" />
            <p className="text-copper text-[10px] tracking-[0.2em] uppercase font-medium mb-6">Inside the Advisory Circle</p>
            <div className="space-y-0">
              {[
                { t: 'Monthly Pulse Session', d: 'One structured working session per month. One documented next move \u2014 not a recommendation. A decision.' },
                { t: 'Quarterly Boardroom Review', d: 'A written external operating read your board can actually use.' },
                { t: 'Annual Operating Sprint', d: 'A 2\u20133 week engagement that ships one specific output for your organization.' },
                { t: 'Decision Desk', d: 'Async support for vendor and build decisions. Written response within 48 hours.' },
              ].map((item, i) => (
                <div key={i} className={`flex gap-4 py-3 ${i < 3 ? 'border-b border-vine-neutral/8' : ''}`}>
                  <span className="text-copper/50 text-[14px] mt-0.5">&rarr;</span>
                  <div>
                    <p className="font-body text-[14px] font-semibold" style={{color:'#3a3530'}}>{item.t}</p>
                    <p className="font-body text-[13px] leading-relaxed" style={{color:'#4a4540'}}>{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </RevealSection>

          <RevealSection>
            <div className="mt-8 p-5 bg-vine-deep text-vine-warm">
              <p className="font-body text-vine-warm/70 text-[14px] leading-relaxed">By Day 90: 12&ndash;20 pages of documented operating intelligence specific to your organization &mdash; plus a scoped Sprint ready to activate.</p>
            </div>
          </RevealSection>

          <RevealSection>
            <div className="mt-8 w-full h-px bg-copper/15" />
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><p className="font-body text-[13px]" style={{color:'#3a3530'}}>Advisory Circle membership</p><p className="font-display text-2xl text-vine-primary">$1,650<span className="text-vine-primary/40 text-[14px] ml-1">/month</span></p></div>
              <div><p className="font-body text-[13px]" style={{color:'#4a4540'}}>Fractional COO at your stage</p><p className="font-display text-2xl text-vine-primary/40">$8,000&ndash;$20,000<span className="text-vine-primary/30 text-[14px] ml-1">/month</span></p></div>
            </div>
            <p className="mt-5 font-body text-[14px] italic" style={{color:'#4a4540'}}>The operating intelligence is the same. The overhead is not.</p>
          </RevealSection>

          <RevealSection>
            <div className="mt-10 w-full h-px bg-copper/15" />
            <div className="mt-8">
              <p className="font-display text-[2rem] md:text-[2.25rem] text-vine-primary font-light leading-tight mb-2">Read the Packet first.<br/>Apply when you are ready.</p>
              <Link to="/apply" className="mt-6 inline-flex items-center gap-2 px-10 py-3.5 text-white text-[13px] tracking-[0.08em] uppercase btn-copper" style={{backgroundColor:'#b5813b'}}>Apply for Membership <ArrowRight size={14} /></Link>
              <p className="mt-4 font-body text-[13px]" style={{color:'#4a4540'}}>The application takes 8 minutes. Review within 3 business days. If it is the right fit, you are in the membership within a week.</p>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ===== 8. FORM — Second Instance ===== */}
      <section className="bg-vine-warm border-t border-vine-neutral/10">
        <div className="max-w-xl mx-auto px-6 lg:px-10 py-14 md:py-16">
          <RevealSection>
            <p className="text-copper text-[11px] tracking-[0.2em] uppercase font-medium mb-3">Get the Operator Packet &mdash; Free</p>
            <div className="p-7 border border-vine-neutral/15 bg-white/40">
              <PacketForm id="packet-form-bottom" variant="second" />
            </div>
          </RevealSection>
        </div>
      </section>
    </div>
  );
}
